// utils/permissionService.ts
//
// Centralised permission checks for the application. Most of these functions
// are not yet called directly by UI components — the Admin page and tabs
// currently use inline entity_type checks instead. We keep the full surface
// here intentionally so that permission logic has a single home and UI
// components can migrate to it incrementally rather than scattering role
// checks across the codebase. The test suite in __tests__/permissionService.test.ts
// exercises every function to keep them honest.
//
// Currently used in app code: canAccessSuperAdmin (TopNav, SystemAdminPage).

import type { AuthUser } from '@/types';
import { CAN_MANAGE_AGENCIES, CAN_MANAGE_CLIENTS } from '@/types/common';

type CreatableEntityType = 'user' | 'agency' | 'client' | 'broadcaster' | 'channel' | 'adtech' | 'adtechgroup';

export const permissionService = {
    // ====================
    // CORE PERMISSION CHECKS
    // ====================

    canManageUsers(user: AuthUser | null): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        return user.isAdmin;
    },

    canManageAgencies(user: AuthUser | null): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        return CAN_MANAGE_AGENCIES.has(user.entity_type);
    },

    canManageClients(user: AuthUser | null): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        return CAN_MANAGE_CLIENTS.has(user.entity_type);
    },

    canManageBroadcasters(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canManageChannels(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canManageAdTech(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canManageAdTechGroups(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canViewActivity(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canAccessSuperAdmin(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    canViewSystemHealth(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isSuperAdmin;
    },

    // ====================
    // ENTITY-SPECIFIC PERMISSIONS
    // ====================

    canEditEntity(user: AuthUser | null, targetEntityId: number): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        return user.isAdmin && user.entity_id === targetEntityId;
    },

    canDeleteEntity(user: AuthUser | null, targetEntityId: number): boolean {
        return this.canEditEntity(user, targetEntityId);
    },

    canCreateEntity(user: AuthUser | null, entityType: CreatableEntityType): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;

        switch (entityType) {
            case 'user':
                return this.canManageUsers(user);
            case 'agency':
                return this.canManageAgencies(user);
            case 'client':
                return this.canManageClients(user);
            case 'broadcaster':
                return this.canManageBroadcasters(user);
            case 'channel':
                return this.canManageChannels(user);
            case 'adtech':
                return this.canManageAdTech(user);
            case 'adtechgroup':
                return this.canManageAdTechGroups(user);
            default:
                return false;
        }
    },

    // ====================
    // CAMPAIGN MANAGEMENT PERMISSIONS
    // ====================

    canManageCampaigns(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    canManageBookings(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    canManageCreatives(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    canManageImpressionTags(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    canManageSiteTags(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    // ====================
    // DASHBOARD PERMISSIONS
    // ====================

    canAccessDashboard(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    canExportData(user: AuthUser | null): boolean {
        if (!user) return false;
        return true;
    },

    // ====================
    // UTILITY METHODS
    // ====================

    getUserPermissionSummary(user: AuthUser | null): Record<string, unknown> {
        if (!user) return { error: 'No user provided' };

        return {
            entity_type: user.entity_type,
            permission_level: user.permission_level,
            entity_id: user.entity_id,
            isAdmin: user.isAdmin,
            isSuperAdmin: user.isSuperAdmin,
            permissions: {
                canManageUsers: this.canManageUsers(user),
                canManageAgencies: this.canManageAgencies(user),
                canManageClients: this.canManageClients(user),
                canManageBroadcasters: this.canManageBroadcasters(user),
                canManageChannels: this.canManageChannels(user),
                canManageAdTech: this.canManageAdTech(user),
                canManageAdTechGroups: this.canManageAdTechGroups(user),
                canViewActivity: this.canViewActivity(user),
                canAccessSuperAdmin: this.canAccessSuperAdmin(user),
                canManageCampaigns: this.canManageCampaigns(user),
                canAccessDashboard: this.canAccessDashboard(user),
            },
        };
    },

    hasAnyAdminPermissions(user: AuthUser | null): boolean {
        if (!user) return false;
        return user.isAdmin || user.isSuperAdmin;
    },

    canManageEntityType(user: AuthUser | null, entityType: string): boolean {
        if (!user) return false;
        if (user.isSuperAdmin) return true;

        switch (entityType.toLowerCase()) {
            case 'adtech':
                return this.canManageAdTech(user);
            case 'adtechgroup':
                return this.canManageAdTechGroups(user);
            case 'distributor':
                return user.isAdmin && user.entity_type === 'DISTRIBUTOR';
            default:
                return user.isAdmin;
        }
    },
};
